// src/components/ErrorBoundary.tsx
import { Typography } from "@mui/material";
import { FlexCol } from "@xylabs/react-flexbox";
import { Component } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var ErrorBoundary = class extends Component {
  constructor(props) {
    super(props);
    this.state = { error: void 0 };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, errorInfo) {
    console.error(`${error}: ${errorInfo}`);
  }
  render() {
    if (this.state.error) {
      if (this.props.fallbackWithError) {
        return this.props.fallbackWithError(this.state.error);
      }
      return this.props.fallback ?? /* @__PURE__ */ jsxs(FlexCol, { children: [
        /* @__PURE__ */ jsx(Typography, { variant: "h1", children: "Something went wrong." }),
        this.props.scope && /* @__PURE__ */ jsxs(Typography, { variant: "h2", children: [
          "[",
          this.props.scope,
          "]"
        ] }),
        /* @__PURE__ */ jsxs(Typography, { variant: "body1", children: [
          "[",
          this.state.error?.message,
          "]"
        ] })
      ] });
    }
    return this.props.children;
  }
};

// src/components/ErrorBoundary/ThrownErrorBoundary.tsx
import { Component as Component2 } from "react";

// src/contexts/ErrorReporter/Context.ts
import { createContext } from "react";
var ErrorReporterContext = createContext({});

// src/contexts/ErrorReporter/Provider.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var ErrorReporterProvider = ({ children, rollbar: rollbarProp }) => {
  const rollbar = rollbarProp ?? globalThis.rollbar;
  if (!rollbar) {
    throw new Error("ErrorReporterProvider unable to find a Rollbar instance either passed as prop or from Provider");
  }
  return /* @__PURE__ */ jsx2(ErrorReporterContext, { value: { rollbar }, children });
};

// src/contexts/ErrorReporter/useErrorReporter.tsx
import { use } from "react";
var useErrorReporter = () => {
  const context = use(ErrorReporterContext);
  if (context === void 0) {
    console.warn("useErrorReporter must be used within a ErrorReporterContext");
  }
  return context ?? {};
};

// src/components/ErrorRender/ErrorAlert.tsx
import { ChevronRight, ExitToApp as ExitIcon } from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Grow,
  Stack,
  Typography as Typography2
} from "@mui/material";
import { ButtonEx } from "@xylabs/react-button";
import { FlexCol as FlexCol2, FlexRow } from "@xylabs/react-flexbox";
import { useState } from "react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function ErrorAlert({
  action,
  additionalMessaging,
  title = "Whoops! Something went wrong",
  onCancel,
  error = "An unknown error occurred",
  scope,
  sx,
  ...props
}) {
  const [openDetails, setOpenDetails] = useState(false);
  return /* @__PURE__ */ jsxs2(
    Alert,
    {
      action: /* @__PURE__ */ jsxs2(FlexRow, { gap: 0.5, children: [
        action,
        onCancel ? /* @__PURE__ */ jsx3(
          ButtonEx,
          {
            color: "error",
            variant: "outlined",
            size: "small",
            onClick: onCancel,
            children: /* @__PURE__ */ jsx3(ExitIcon, { fontSize: "small" })
          }
        ) : null
      ] }),
      severity: "error",
      sx: { maxWidth: "100%", ...sx },
      ...props,
      children: [
        /* @__PURE__ */ jsx3(AlertTitle, { children: title }),
        /* @__PURE__ */ jsxs2(FlexCol2, { width: "100%", alignItems: "start", justifyContent: "center", gap: 1, children: [
          additionalMessaging,
          /* @__PURE__ */ jsxs2(FlexRow, { gap: 0.5, sx: { cursor: "pointer" }, onClick: () => setOpenDetails(!openDetails), children: [
            /* @__PURE__ */ jsx3(Typography2, { variant: "caption", fontWeight: "bold", children: "Details:" }),
            openDetails ? /* @__PURE__ */ jsx3(ChevronRight, { sx: { fontSize: "1.1rem", transform: "rotate(90deg)" } }) : /* @__PURE__ */ jsx3(ChevronRight, { sx: { fontSize: "1.1rem" } })
          ] }),
          /* @__PURE__ */ jsx3(Grow, { in: openDetails, unmountOnExit: true, children: /* @__PURE__ */ jsxs2(Stack, { sx: { hyphens: "auto", maxWidth: "100%" }, children: [
            scope ? /* @__PURE__ */ jsxs2(Stack, { direction: "row", gap: 0.25, sx: { maxWidth: "100%" }, children: [
              /* @__PURE__ */ jsx3(Typography2, { variant: "caption", mr: 0.5, fontWeight: "bold", children: "Scope:" }),
              /* @__PURE__ */ jsx3(Typography2, { variant: "caption", children: scope })
            ] }) : null,
            /* @__PURE__ */ jsxs2("div", { children: [
              /* @__PURE__ */ jsx3(Typography2, { variant: "caption", mr: 0.5, fontWeight: "bold", children: "Error:" }),
              /* @__PURE__ */ jsx3(Typography2, { variant: "caption", children: typeof error === "string" ? error : error?.message })
            ] })
          ] }) })
        ] })
      ]
    }
  );
}

// src/components/ErrorRender/ErrorRenderWithSupport.tsx
import { BugReport } from "@mui/icons-material";
import {
  IconButton,
  Link,
  Tooltip
} from "@mui/material";
import { useMemo } from "react";

// src/components/ErrorRender/Render.tsx
import { FlexCol as FlexCol3 } from "@xylabs/react-flexbox";
import { useEffect } from "react";
import { Fragment, jsx as jsx4 } from "react/jsx-runtime";
function ErrorRender({
  onCancel,
  error,
  noErrorDisplay = false,
  customError = null,
  children,
  scope,
  useLocation,
  slotProps,
  sx,
  ...props
}) {
  const location = useLocation?.();
  useEffect(() => {
    if (location) {
      location.state = { from: { pathname: globalThis.location.pathname } };
    }
  }, [location]);
  useEffect(() => {
    if (error) {
      globalThis.rollbar?.error(error);
    }
  }, [error]);
  return error ? /* @__PURE__ */ jsx4(FlexCol3, { alignItems: "stretch", ...props, children: noErrorDisplay ? customError : /* @__PURE__ */ jsx4(FlexCol3, { alignItems: "center", ...props, children: /* @__PURE__ */ jsx4(ErrorAlert, { error, onCancel, scope, sx: { position: "relative", ...sx }, ...slotProps?.alert }) }) }) : /* @__PURE__ */ jsx4(Fragment, { children });
}

// src/components/ErrorRender/ErrorRenderWithSupport.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var ErrorRenderWithSupport = ({
  supportHref,
  supportIcon,
  supportLinkTitle = "Support",
  slotProps,
  ...props
}) => {
  const combinedSlotProps = useMemo(() => ({
    ...slotProps,
    alert: {
      ...slotProps?.alert,
      action: supportHref && /* @__PURE__ */ jsx5(Link, { href: supportHref, target: "_blank", children: /* @__PURE__ */ jsx5(Tooltip, { title: supportLinkTitle, children: /* @__PURE__ */ jsx5(IconButton, { color: "error", size: "small", children: supportIcon ?? /* @__PURE__ */ jsx5(BugReport, {}) }) }) })
    }
  }), [slotProps, supportHref]);
  return /* @__PURE__ */ jsx5(
    ErrorRender,
    {
      slotProps: combinedSlotProps,
      ...props
    }
  );
};

// src/components/ErrorBoundary/ThrownErrorBoundary.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var ThrownErrorBoundaryInner = class _ThrownErrorBoundaryInner extends Component2 {
  state = { errorEx: void 0 };
  static getDerivedStateFromError(error) {
    const thrownErrorBoundaryState = {
      hasError: true,
      errorEx: error,
      xyoError: _ThrownErrorBoundaryInner.normalizeError(error)
    };
    return thrownErrorBoundaryState;
  }
  static normalizeError(error) {
    return error;
  }
  componentDidCatch(error, errorInfo) {
    const { rethrow, rollbar } = this.props;
    const { errorEx } = this.state;
    rollbar?.error(error);
    console.error("Error:", errorEx, errorInfo);
    if (rethrow) {
      throw error;
    }
  }
  render() {
    const { errorEx } = this.state;
    const {
      children,
      boundaryName,
      errorComponent,
      scope,
      title
    } = this.props;
    if (errorEx) {
      if (errorComponent) {
        return errorComponent(errorEx, boundaryName);
      }
      return /* @__PURE__ */ jsx6(ErrorRender, { error: errorEx, errorContext: `${boundaryName} Boundary`, scope, title });
    }
    return children;
  }
};
function ThrownErrorBoundary({ rollbar, ...props }) {
  const { rollbar: rollbarErrorReporter } = useErrorReporter();
  return /* @__PURE__ */ jsx6(ThrownErrorBoundaryInner, { rollbar: rollbar ?? rollbarErrorReporter ?? globalThis.rollbar, ...props });
}

// src/components/ErrorQuickTipButton.tsx
import { CancelRounded as CancelRoundedIcon, CheckCircleOutlineRounded as CheckCircleOutlineRoundedIcon } from "@mui/icons-material";
import { Typography as Typography3 } from "@mui/material";
import { FlexCol as FlexCol4 } from "@xylabs/react-flexbox";
import { QuickTipButton } from "@xylabs/react-quick-tip-button";
import { isDefined } from "@xylabs/typeof";
import { jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var InvalidIcon = ({ ref, ...props }) => /* @__PURE__ */ jsx7(CancelRoundedIcon, { color: "error", ref, ...props });
InvalidIcon.displayName = "InvalidIcon";
var ErrorQuickTipButton = ({
  boundWitnessType = "Bound Witness",
  errors,
  iconColors,
  validateError,
  ...props
}) => {
  const hasErrors = isDefined(errors) && errors.length > 0;
  return /* @__PURE__ */ jsx7(
    QuickTipButton,
    {
      Icon: hasErrors ? ({ ref, ...props2 }) => {
        return /* @__PURE__ */ jsx7(InvalidIcon, { color: iconColors ? "error" : void 0, ref, ...props2 });
      } : ({ ref, ...props2 }) => {
        return /* @__PURE__ */ jsx7(CheckCircleOutlineRoundedIcon, { color: iconColors ? "success" : void 0, ref, ...props2 });
      },
      hoverText: hasErrors ? `Invalid ${boundWitnessType}` : "Valid",
      ...props,
      children: hasErrors ? /* @__PURE__ */ jsxs3(FlexCol4, { flexWrap: "wrap", alignItems: "start", children: [
        /* @__PURE__ */ jsx7(ErrorRender, { error: validateError, scope: "VerificationError" }),
        errors.map((error) => {
          return /* @__PURE__ */ jsx7(Typography3, { children: error.toString() }, error.name);
        })
      ] }) : /* @__PURE__ */ jsx7(Typography3, { children: "No Errors" })
    }
  );
};

// src/components/Errors/ErrorsViewer.tsx
import { Replay as ReplayIcon } from "@mui/icons-material";
import { IconButton as IconButton2 } from "@mui/material";
import { FlexGrowCol } from "@xylabs/react-flexbox";

// src/components/Errors/ErrorViewer.tsx
import { Typography as Typography4, useTheme } from "@mui/material";
import { FlexCol as FlexCol5 } from "@xylabs/react-flexbox";
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
var ErrorViewer = (props) => {
  const { error, ...boxProps } = props;
  const theme = useTheme();
  return /* @__PURE__ */ jsxs4(FlexCol5, { color: theme.vars.palette.error.main, ...boxProps, children: [
    /* @__PURE__ */ jsx8(Typography4, { align: "center", variant: "subtitle1", children: error?.name }),
    /* @__PURE__ */ jsx8(Typography4, { align: "center", variant: "body2", children: error?.message })
  ] });
};

// src/components/Errors/ErrorsViewer.tsx
import { jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
var ErrorsViewer = (props) => {
  const {
    onRetry,
    errors,
    ...boxProps
  } = props;
  return /* @__PURE__ */ jsxs5(FlexGrowCol, { padding: 1, ...boxProps, children: [
    errors?.map((error, index) => {
      return /* @__PURE__ */ jsx9(ErrorViewer, { error }, index);
    }),
    onRetry ? /* @__PURE__ */ jsx9(IconButton2, { onClick: onRetry, children: /* @__PURE__ */ jsx9(ReplayIcon, {}) }) : null
  ] });
};
export {
  ErrorAlert,
  ErrorBoundary,
  ErrorQuickTipButton,
  ErrorRender,
  ErrorRenderWithSupport,
  ErrorReporterProvider,
  ErrorViewer,
  ErrorsViewer,
  ThrownErrorBoundary,
  useErrorReporter
};
//# sourceMappingURL=index.mjs.map